import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { currencySymbol } from "@/lib/constants";

/**
 * Edit dialog for an existing invoice — line items, tax rate, due date, payment terms, notes.
 * The customer / bill-to is fixed once the invoice is generated (per the job snapshot).
 */
export default function InvoiceEditDialog({ open, onOpenChange, invoice, onSaved }) {
  const [lines, setLines] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [dueDate, setDueDate] = useState("");
  const [terms, setTerms] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      setLines((invoice.lines || []).map((l) => ({
        description: l.description || "",
        quantity: l.quantity ?? 1,
        unit_price: l.unit_price ?? 0,
      })));
      setTaxRate(invoice.tax_rate ?? 0);
      setDueDate(invoice.due_date || "");
      setTerms(invoice.payment_terms || "Net 30 days");
      setNotes(invoice.notes || "");
    }
  }, [open, invoice]);

  if (!invoice) return null;
  const sym = currencySymbol(invoice.currency);

  const subtotal = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  const tax = subtotal * (Number(taxRate) || 0) / 100;
  const total = subtotal + tax;

  const addLine = () => setLines([...lines, { description: "", quantity: 1, unit_price: 0 }]);
  const setLine = (i, k, v) => {
    const next = [...lines];
    next[i] = { ...next[i], [k]: v };
    setLines(next);
  };
  const rmLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (lines.some((l) => !l.description.trim())) {
      toast.error("Every line needs a description.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        lines: lines.map((l) => ({
          description: l.description.trim(),
          quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0,
        })),
        tax_rate: Number(taxRate) || 0,
        due_date: dueDate || null,
        payment_terms: terms || null,
        notes: notes || null,
      };
      const { data } = await api.patch(`/invoices/${invoice.id}`, payload);
      toast.success(`Invoice ${data.invoice_number} updated`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sharp max-w-3xl max-h-[90vh] overflow-y-auto border-zinc-950">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight">Edit invoice {invoice.invoice_number}</DialogTitle>
          <DialogDescription className="text-sm text-zinc-600">
            Bill-to: <span className="font-semibold">{invoice.customer_company || invoice.customer_name}</span>
            {invoice.customer_company && invoice.customer_name && <> ({invoice.customer_name})</>}
            {" · "}customer cannot be changed after invoice generation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <div className="flex items-center justify-between">
              <div className="mono-label">LINE ITEMS</div>
              <Button type="button" size="sm" variant="outline" onClick={addLine} className="sharp h-8 border-zinc-400 bg-white" data-testid="inv-add-line">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add line
              </Button>
            </div>
            <div className="mt-2 border border-zinc-950">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-zinc-950 text-white mono-label !text-zinc-100">
                <div className="col-span-6">Description</div>
                <div className="col-span-2 text-right">Qty</div>
                <div className="col-span-3 text-right">Unit price</div>
                <div className="col-span-1"></div>
              </div>
              <div className="divide-y divide-zinc-200">
                {lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`inv-line-${i}`}>
                    <Input
                      value={l.description}
                      onChange={(e) => setLine(i, "description", e.target.value)}
                      className="sharp col-span-6 apex-input"
                      placeholder="Freight, fuel surcharge, detention…"
                      data-testid={`inv-line-desc-${i}`}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={l.quantity}
                      onChange={(e) => setLine(i, "quantity", e.target.value)}
                      className="sharp col-span-2 apex-input text-right font-mono"
                      data-testid={`inv-line-qty-${i}`}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={l.unit_price}
                      onChange={(e) => setLine(i, "unit_price", e.target.value)}
                      className="sharp col-span-3 apex-input text-right font-mono"
                      data-testid={`inv-line-price-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => rmLine(i)}
                      className="col-span-1 h-9 flex items-center justify-center border border-zinc-300 text-rose-700 hover:bg-rose-50"
                      title="Remove line"
                      data-testid={`inv-line-rm-${i}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {lines.length === 0 && (
                  <div className="px-3 py-4 text-center text-sm text-zinc-500">No line items — click Add line to start.</div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mono-label">Tax rate (%)</label>
              <Input type="number" step="0.01" value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                className="sharp mt-1 apex-input font-mono" data-testid="inv-tax-rate" />
            </div>
            <div>
              <label className="mono-label">Due date</label>
              <Input type="date" value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="sharp mt-1 apex-input" data-testid="inv-due-date" />
            </div>
            <div>
              <label className="mono-label">Payment terms</label>
              <Input value={terms}
                onChange={(e) => setTerms(e.target.value)}
                className="sharp mt-1 apex-input" data-testid="inv-terms" />
            </div>
          </div>

          <div>
            <label className="mono-label">Notes (internal)</label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="sharp mt-1 apex-input" />
          </div>

          <div className="border border-zinc-950 bg-zinc-50 p-3 text-sm">
            <div className="flex items-center justify-between"><span className="mono-label">Subtotal</span><span className="font-mono">{sym}{subtotal.toFixed(2)}</span></div>
            <div className="flex items-center justify-between"><span className="mono-label">Tax ({Number(taxRate) || 0}%)</span><span className="font-mono">{sym}{tax.toFixed(2)}</span></div>
            <div className="flex items-center justify-between border-t border-zinc-300 mt-2 pt-2 font-bold text-lg"><span className="mono-label !text-zinc-900">TOTAL {invoice.currency}</span><span className="font-mono text-[#0056B3]" data-testid="inv-edit-total">{sym}{total.toFixed(2)}</span></div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="sharp border-zinc-300">Cancel</Button>
            <Button type="submit" disabled={busy} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="inv-edit-submit">
              {busy ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
